/**
 * Calling in a ratio: the share boxes on a work profile, and the sentence that
 * says what the numbers will actually do.
 *
 * The rule itself is in internal/queue/calling.go. A profile on it gives each of
 * its queues a small whole number, and the desk calls from whichever queue has
 * fallen furthest below its share of the recent calling. Two Platinum for each
 * Gold, without Silver being abandoned the way strict priority abandons it.
 *
 * All of it lives here rather than in the screen because the web suite runs in
 * Node with no DOM and cannot render a component. A refusal written into the form
 * is a refusal nothing tests; a sentence built in the form is a sentence nobody
 * checks until a branch manager reads it off a screen and believes it.
 */

import { t, type StaffLanguage } from './staffText'

/** The range the column, branchcfg.Validate and checkProfile all hold to. */
export const SHARE_MIN = 1
export const SHARE_MAX = 99

/**
 * How much of the calling this queue is entitled to.
 *
 * A queue the profile serves and the shares do not name has a share of one, which
 * is Profile.ShareOf in the Go and is the whole reason a profile switched to this
 * rule works sensibly before anybody has typed anything: it calls its queues
 * evenly. Zero is deliberately not "never call" anywhere in this product — a queue
 * a desk should not call is a queue it should not list.
 *
 * This is what the rule will do with the number, not what the box shows. Anything
 * outside the range can only have come from a row edited by hand, and ShareOf in
 * the Go reads it the same way, because the rule still has to answer. The box is
 * fed the stored number instead, so that a share somebody has just typed badly
 * stays on screen beside the sentence refusing it rather than quietly becoming a
 * number nobody entered.
 */
export function shareOf(shares: Record<string, number> | undefined, queueId: string): number {
  const share = shares?.[queueId]
  if (typeof share !== 'number' || !Number.isInteger(share) || share < SHARE_MIN) return SHARE_MIN
  return Math.min(share, SHARE_MAX)
}

/** What is wrong with one share, or null. */
export function shareProblem(share: number, lang: StaffLanguage): string | null {
  if (!Number.isInteger(share) || share < SHARE_MIN || share > SHARE_MAX)
    return t('calling.share.outOfRange', lang, { min: SHARE_MIN, max: SHARE_MAX })
  return null
}

/**
 * What is wrong with the whole ratio, in the order it would be met.
 *
 * The second refusal should be unreachable from this screen, because removing a
 * queue takes its share with it — see sharesForSave. It is checked anyway: it is
 * one of the two things the server refuses, and a form that can only say one of
 * them leaves the other arriving as a surprise at Publish.
 */
export function sharesProblem(
  queueIds: readonly string[],
  shares: Record<string, number> | undefined,
  lang: StaffLanguage,
): string | null {
  for (const [queueId, share] of Object.entries(shares ?? {})) {
    const bad = shareProblem(share, lang)
    if (bad) return bad
    if (!queueIds.includes(queueId)) return t('calling.share.notServed', lang)
  }
  return null
}

/**
 * The ratio as it goes back to the server.
 *
 * Two things it does beyond copying. It drops the share of any queue the profile
 * no longer serves, so that removing a queue does not leave behind a number the
 * server refuses and the screen no longer shows a box for. And it sorts the keys,
 * because Go marshals a map with its keys sorted and this screen decides whether a
 * row has unsaved changes by comparing the draft and the stored profile as text —
 * built in any other order, every profile with a ratio would claim to be unsaved
 * from the moment it loaded.
 *
 * Nothing at all, rather than an empty object, when no share is set: an empty
 * object is also a difference, with the same consequence.
 */
export function sharesForSave(
  queueIds: readonly string[],
  shares: Record<string, number> | undefined,
): Record<string, number> | undefined {
  const kept = Object.entries(shares ?? {})
    .filter(([queueId]) => queueIds.includes(queueId))
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  if (kept.length === 0) return undefined
  return Object.fromEntries(kept)
}

/**
 * The ratio as it goes over the wire: always an object, holding only the queues
 * the profile serves.
 *
 * The draft holds nothing when no share is set — see sharesForSave — and the
 * server reads an absent key as "this save did not ask about the ratio" and keeps
 * whatever the profile had. So removing the one queue that had a share left the
 * key out, the server kept the share for a queue the profile no longer served, and
 * refused the save for it: a fault the screen showed no box to correct. Sent as an
 * object every time, an empty one says there is no longer a ratio.
 */
export function sharesToSend(
  queueIds: readonly string[],
  shares: Record<string, number> | undefined,
): Record<string, number> {
  return sharesForSave(queueIds, shares) ?? {}
}

/** A queue as the sentence names it, with the share it has been given. */
export interface QueueShare {
  label: string
  share: number
}

/**
 * What these numbers will actually do, in one sentence.
 *
 * A ratio typed as numbers is a ratio nobody has checked, and this is the check.
 * Which makes it the one thing on the screen that must not flatter the rule.
 *
 * So it states proportions over a cycle and not a running order. Orchestra's
 * manual documents its own worked example as the fixed cycle Platinum, Platinum,
 * Gold, Silver, and ours does not produce that: it asks who has fallen furthest
 * behind their share rather than walking a list, so it keeps the proportions and
 * spreads the busier queue's calls through the cycle instead of bunching them at
 * the front. That is the better behaviour for a hall, and it is not the sentence
 * "two Platinum, then one Gold, then one Silver" promises. The clause about being
 * spread is there because a branch manager who reads a running order here will
 * stand in the hall, see Gold called second, and conclude the product is broken.
 *
 * It is also silent about the cold start — from an empty window nothing is behind
 * anything and the rule falls through to longest waiting for a few calls — because
 * that is a minute of the first morning and this sentence is about the ratio. The
 * field's own hint carries it.
 */
export function ratioSentence(queues: readonly QueueShare[], lang: StaffLanguage): string {
  if (queues.length === 0) return t('calling.ratio.none', lang)
  if (queues.length === 1) {
    return t('calling.ratio.one', lang, { queue: queues[0]!.label })
  }

  const total = queues.reduce((sum, q) => sum + q.share, 0)
  const parts = queues.map((q, index) =>
    index === 0
      ? t('calling.ratio.first', lang, { count: count(q.share, lang), queue: q.label, n: q.share })
      : t('calling.ratio.part', lang, { count: count(q.share, lang), queue: q.label }),
  )
  return t('calling.ratio.sentence', lang, { total: count(total, lang), parts: list(parts, lang) })
}

/**
 * A small number in words, a larger one in figures.
 *
 * Up to twelve a branch manager reads the sentence rather than counting it. Past
 * that the word is harder to read than the number, and a ratio of forty to three
 * is a ratio worth seeing as digits anyway. Azerbaijani and Russian write digits
 * throughout — see phrases/calling.ts's opening comment for why a template
 * cannot spell either language's numbers correctly on its own.
 */
function count(n: number, lang: StaffLanguage): string {
  if (lang !== 'en') return String(n)
  const words = [
    'zero', 'one', 'two', 'three', 'four', 'five', 'six',
    'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve',
  ]
  return words[n] ?? String(n)
}

/** "a, b and c" — the way it is said aloud, with no comma before the and. */
function list(parts: readonly string[], lang: StaffLanguage): string {
  if (parts.length <= 1) return parts.join('')
  return `${parts.slice(0, -1).join(', ')}${t('calling.listAnd', lang)}${parts[parts.length - 1]}`
}
